"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  revealField,
  WordChips,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { normalizeLookup } from "@/lib/tool-kit/vocabulary/datamuse";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";
import {
  COLLOCATION_MODES,
  type CollocationMode,
  collocatesOf,
  collocationQuery,
  directionOfQuery,
  MODE_ORDER,
  wordOfCollocationQuery,
} from "@/lib/tool-kit/word-knowledge/collocations";

type Sort = "strength" | "alphabetical";

/** English strings. A translated content file overrides these through `ui`. */
export const COLLOCATION_FINDER_STRINGS = {
  modeLabel: "Pattern",
  modes: {
    nounsAfterVerb: "Nouns after a verb (conduct → research)",
    verbsBeforeNoun: "Verbs before a noun (pay → attention)",
    verbsAfterNoun: "Verbs after a noun (research → shows)",
    adverbsBeforeAdjective:
      "Adverbs before an adjective (statistically → significant)",
    anyAfter: "Any word after",
    anyBefore: "Any word before",
  } as Record<CollocationMode, string>,
  wordLabels: {
    nounsAfterVerb: "Verb",
    verbsBeforeNoun: "Noun",
    verbsAfterNoun: "Noun",
    adverbsBeforeAdjective: "Adjective",
    anyAfter: "Word",
    anyBefore: "Word",
  } as Record<CollocationMode, string>,
  placeholders: {
    nounsAfterVerb: "conduct",
    verbsBeforeNoun: "attention",
    verbsAfterNoun: "research",
    adverbsBeforeAdjective: "significant",
    anyAfter: "pay",
    anyBefore: "evidence",
  } as Record<CollocationMode, string>,
  wordHint:
    "One word. Results appear as you type; click a pair to explore from its other word.",
  sortLabel: "Order",
  sorts: {
    strength: "Strongest pairs first",
    alphabetical: "Alphabetical",
  } as Record<Sort, string>,
  functionWords: "Include grammar words (the, of, is, and so on)",
  tryLabel: "Try:",
  examples: {
    nounsAfterVerb: ["conduct", "draw", "make", "reach"],
    verbsBeforeNoun: ["attention", "evidence", "conclusion", "role"],
    verbsAfterNoun: ["results", "study", "data", "research"],
    adverbsBeforeAdjective: [
      "significant",
      "important",
      "effective",
      "different",
    ],
    anyAfter: ["pay", "play", "take", "strong"],
    anyBefore: ["evidence", "argument", "attention", "interest"],
  } as Record<CollocationMode, string[]>,
  emptyPrompts: {
    nounsAfterVerb:
      "Type a verb to see the nouns that most often come right after it.",
    verbsBeforeNoun:
      "Type a noun to see the verbs that most often come right before it.",
    verbsAfterNoun:
      "Type a noun to see the verbs that most often come right after it.",
    adverbsBeforeAdjective:
      "Type an adjective to see the adverbs that most often come right before it.",
    anyAfter: "Type a word to see the words that most often follow it.",
    anyBefore: "Type a word to see the words that most often precede it.",
  } as Record<CollocationMode, string>,
  headings: {
    nounsAfterVerb: "Nouns that follow “{word}”",
    verbsBeforeNoun: "Verbs that come before “{word}”",
    verbsAfterNoun: "Verbs that follow “{word}”",
    adverbsBeforeAdjective: "Adverbs that come before “{word}”",
    anyAfter: "Words that follow “{word}”",
    anyBefore: "Words that come before “{word}”",
  } as Record<CollocationMode, string>,
  pairCount: "one:{count} pair|other:{count} pairs",
  orderHints: {
    strength:
      "Strongest first, as ranked by how often the pair appears together in Google Books. Frequent is not the same as correct, so check a pair that matters against a dictionary or your style guide.",
    alphabetical: "In alphabetical order.",
  } as Record<Sort, string>,
  noResults:
    "Nothing of that kind was found for “{word}”. Check the spelling, try another pattern, or choose “Any word” to see every neighbour.",
  adjacentNote:
    "Only words that sit directly beside “{word}” are counted, so a pair with an article between its words, like “make a decision”, shows up from the verb (make → decisions), not from the noun.",
  copyList: "Copy list",
  copied: "Copied!",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  credit: "Word data from the Datamuse API",
  sourceNote:
    "Pairs are counted in the Google Books Ngrams corpus, so they reflect published English, mostly written. Each lookup is sent to CitationEasy's server, which asks the Datamuse word service and keeps answers in a cache. Parts of speech come from Datamuse's own tags, which are loose: a few results are the wrong kind and a few words are missing.",
} as const;

type Strings = typeof COLLOCATION_FINDER_STRINGS;

const FIELD_ID = "colloc-word";
const MAX_INPUT = 30;
const RE_LETTER = /\p{L}/u;
const SORTS: Sort[] = ["strength", "alphabetical"];

function firstWord(input: string): string {
  const [first = ""] = normalizeLookup(input).split(" ");
  return RE_LETTER.test(first) ? first : "";
}

function Results({
  mode,
  word,
  phrases,
  words,
  stale,
  strings,
  onSelect,
}: {
  mode: CollocationMode;
  word: string;
  phrases: readonly string[];
  words: readonly string[];
  stale: boolean;
  strings: Strings;
  onSelect: (word: string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (phrases.length === 0) {
    return <Notice>{t(strings.noResults, { word })}</Notice>;
  }
  return (
    <div className={stale ? "space-y-4 opacity-60" : "space-y-4"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div aria-live="polite" className="space-y-1">
          <h2 className={WIDGET_H2}>{t(strings.headings[mode], { word })}</h2>
          <p className={HINT}>
            {tPlural(strings.pairCount, locale, phrases.length, {
              count: fmt(phrases.length),
            })}
          </p>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyList}
          small
          text={phrases.join("\n")}
        />
      </div>
      <WordChips
        format={(entry) => phrases[words.indexOf(entry)] ?? entry}
        onSelect={onSelect}
        words={words}
      />
    </div>
  );
}

export default function CollocationFinderWidget() {
  const strings = useToolUi(COLLOCATION_FINDER_STRINGS);
  const [mode, setMode] = useState<CollocationMode>("nounsAfterVerb");
  const [sort, setSort] = useState<Sort>("strength");
  const [functionWords, setFunctionWords] = useState(false);
  const [input, setInput] = useState("");
  const word = firstWord(input);
  const lookup = useDatamuse(word ? [collocationQuery(word, mode)] : []);
  const answeredWord = wordOfCollocationQuery(lookup.answered);
  const sameSide =
    directionOfQuery(lookup.answered) === COLLOCATION_MODES[mode].direction;
  const answer = word && sameSide ? lookup.results[0] : undefined;
  const found = answer
    ? collocatesOf(answeredWord, mode, answer, {
        includeFunctionWords: functionWords,
      })
    : [];
  const ordered =
    sort === "alphabetical"
      ? [...found].sort((a, b) => a.word.localeCompare(b.word))
      : found;

  function lookUp(next: string) {
    setInput(next);
    revealField(FIELD_ID);
  }

  /** A clicked pair looks up its other word, from that word's side. */
  function explore(result: string) {
    setMode(COLLOCATION_MODES[mode].next);
    lookUp(result);
  }

  return (
    <>
      <SelectField
        id="colloc-mode"
        label={strings.modeLabel}
        onChange={setMode}
        options={MODE_ORDER.map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <TextField
          hint={strings.wordHint}
          id={FIELD_ID}
          label={strings.wordLabels[mode]}
          onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
          placeholder={strings.placeholders[mode]}
          type="search"
          value={input}
        />
        <SelectField
          id="colloc-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={SORTS.map((value) => ({
            value,
            label: strings.sorts[value],
          }))}
          value={sort}
        />
      </div>
      <Checkbox
        checked={functionWords}
        label={strings.functionWords}
        onChange={setFunctionWords}
      />
      {word ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompts[mode]}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples[mode]}
          />
        </div>
      )}
      {answer ? (
        <>
          <Results
            mode={mode}
            onSelect={explore}
            phrases={ordered.map((entry) => entry.phrase)}
            stale={lookup.status === "loading"}
            strings={strings}
            word={answeredWord}
            words={ordered.map((entry) => entry.word)}
          />
          <p className={HINT}>
            {t(strings.adjacentNote, { word: answeredWord })}
          </p>
          <p className={HINT}>{strings.orderHints[sort]}</p>
        </>
      ) : null}
      <DatamuseCredit label={strings.credit} />
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
